import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { Calendar, RefreshCw } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';
const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';

const DATE_OPTIONS = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'last6', label: 'Last 6 months' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom range' }
];

const formatCurrency = (value, currency) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0
    }).format(number);
  } catch (e) {
    return Math.round(number).toLocaleString();
  }
};

export default function NewVsReturningRevenue() {
  const { selectedBrand } = useAuth();
  const [dateRange, setDateRange] = useState('last6');
  const [viewMode, setViewMode] = useState('monthly');
  const [customDates, setCustomDates] = useState({ startDate: '', endDate: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [sources, setSources] = useState(null);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState('');
  const [sourceMetric, setSourceMetric] = useState('newUsers');
  const [lastUpdated, setLastUpdated] = useState(null);
  const isMonthly = viewMode === 'monthly';
  const isWeekly = viewMode === 'weekly';
  const isBucketed = isMonthly || isWeekly;
  const isMonthlyData = payload?.granularity === 'monthly';
  const isWeeklyData = payload?.granularity === 'weekly';
  const isBucketedData = isMonthlyData || isWeeklyData;

  const normalizedRows = useMemo(() => {
    if (isBucketed) return [];
    const list = Array.isArray(payload?.data) ? payload.data : [];
    const mapped = list.map((row) => {
      const raw = String(row.userType || '').toLowerCase();
      let key = raw;
      if (raw.includes('new')) key = 'new';
      if (raw.includes('return')) key = 'returning';
      return {
        key,
        label: key === 'new' ? 'New Users' : key === 'returning' ? 'Returning Users' : raw || 'Other',
        totalRevenue: Number(row.totalRevenue || 0),
        totalUsers: Number(row.totalUsers || 0),
      };
    });
    return mapped.sort((a, b) => b.totalRevenue - a.totalRevenue);
  }, [payload, isBucketed]);

  // Format a Monday-anchored week-start date as "Apr 27 – May 3 2026".
  const formatWeekLabel = (wk) => {
    const s = new Date(wk + 'T00:00:00');
    if (isNaN(s)) return wk;
    const e = new Date(s); e.setDate(e.getDate() + 6);
    const f = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${f(s)} – ${f(e)} ${e.getFullYear()}`;
  };

  const monthlyRows = useMemo(() => {
    if (!isBucketed || !isBucketedData) return [];
    const list = Array.isArray(payload?.data) ? payload.data : [];
    return list
      .map((row) => {
      const totalRevenue = (row?.new?.revenue || 0) + (row?.returning?.revenue || 0) + (row?.notSet?.revenue || 0);
      const bucketKey = row?.month || row?.week || '';
      return {
        // `month` is kept for backward compatibility with existing render code.
        month: bucketKey,
        bucketLabel: row?.week ? formatWeekLabel(row.week) : bucketKey,
        newRevenue: Number(row?.new?.revenue || 0),
        returningRevenue: Number(row?.returning?.revenue || 0),
        notSetRevenue: Number(row?.notSet?.revenue || 0),
        newUsers: Number(row?.new?.users || 0),
        returningUsers: Number(row?.returning?.users || 0),
        notSetUsers: Number(row?.notSet?.users || 0),
        totalRevenue
      };
      })
      .sort((a, b) => (a.month < b.month ? 1 : -1));
  }, [payload, isBucketed, isBucketedData]);

  const monthlyTotals = useMemo(() => {
    if (!isBucketed || !isBucketedData) return { totalRevenue: 0, totalUsers: 0, newUsers: 0, returningUsers: 0 };
    return monthlyRows.reduce(
      (acc, row) => {
        acc.totalRevenue += row.totalRevenue;
        acc.totalUsers += row.newUsers + row.returningUsers + row.notSetUsers;
        acc.newUsers += row.newUsers;
        acc.returningUsers += row.returningUsers;
        return acc;
      },
      { totalRevenue: 0, totalUsers: 0, newUsers: 0, returningUsers: 0 }
    );
  }, [monthlyRows, isBucketed, isBucketedData]);

  const rawTotals = payload?.totals || { totalRevenue: 0, totalUsers: 0, newUsers: 0 };
  const nonMonthlyTotals = useMemo(() => {
    const newRow = normalizedRows.find((r) => r.key === 'new');
    const retRow = normalizedRows.find((r) => r.key === 'returning');
    return {
      ...rawTotals,
      newUsers: newRow?.totalUsers || 0,
      returningUsers: retRow?.totalUsers || 0
    };
  }, [rawTotals, normalizedRows]);
  const displayTotals = isBucketed ? monthlyTotals : nonMonthlyTotals;

  const revenueBreakdown = useMemo(() => {
    if (isBucketed) {
      const returning = monthlyRows.reduce((sum, row) => sum + (row.returningRevenue || 0), 0);
      const news = monthlyRows.reduce((sum, row) => sum + (row.newRevenue || 0), 0);
      return { returning, news };
    }
    const returningRow = normalizedRows.find((row) => row.key === 'returning');
    const newRow = normalizedRows.find((row) => row.key === 'new');
    return {
      returning: returningRow?.totalRevenue || 0,
      news: newRow?.totalRevenue || 0
    };
  }, [isBucketed, monthlyRows, normalizedRows]);

  const returningShare = displayTotals.totalRevenue > 0
    ? (revenueBreakdown.returning / displayTotals.totalRevenue) * 100
    : 0;
  const newShare = displayTotals.totalRevenue > 0
    ? (revenueBreakdown.news / displayTotals.totalRevenue) * 100
    : 0;

  const sourceColumns = useMemo(() => {
    const list = Array.isArray(sources?.columns) ? sources.columns : [];
    return list.map((col) => ({
      key: col?.key,
      label: col?.label || col?.key,
      isOther: !!col?.isOther,
      groupCount: Number(col?.groupCount || 0)
    }));
  }, [sources]);

  const sourceRows = useMemo(() => {
    const list = Array.isArray(sources?.data) ? sources.data : [];
    return list.map((row) => {
      const newUsers = Number(row?.new?.users || 0);
      const returningUsers = Number(row?.returning?.users || 0);
      const notSetUsers = Number(row?.notSet?.users || 0);
      const totalUsers = Number(row?.totalUsers || 0) || (newUsers + returningUsers + notSetUsers);
      return {
        key: row?.key || row?.label,
        label: row?.label || row?.key || '(not set)',
        isOther: !!row?.isOther,
        groupCount: Number(row?.groupCount || 0),
        topSources: Array.isArray(row?.topSources) ? row.topSources : [],
        newUsers,
        returningUsers,
        notSetUsers,
        totalUsers,
        newRevenue: Number(row?.new?.revenue || 0),
        returningRevenue: Number(row?.returning?.revenue || 0),
        totalRevenue: Number(row?.totalRevenue || 0),
        paidUsers: Number(row?.paidUsers || 0),
        paidSharePct: totalUsers > 0 ? (Number(row?.paidUsers || 0) / totalUsers) * 100 : 0,
        newSharePct: totalUsers > 0 ? (newUsers / totalUsers) * 100 : 0,
        returningSharePct: totalUsers > 0 ? (returningUsers / totalUsers) * 100 : 0
      };
    });
  }, [sources]);

  const sourceTotals = sources?.totals || null;

  // Monthly matrix: newest month first, same column order as the table above.
  const sourceMonthly = useMemo(() => {
    const list = Array.isArray(sources?.monthly) ? sources.monthly : [];
    return [...list].sort((a, b) => (a.month < b.month ? 1 : -1));
  }, [sources]);

  const SOURCE_METRICS = [
    { key: 'newUsers', label: 'New Users', pick: (e) => Number(e?.new?.users || 0), money: false },
    { key: 'returningUsers', label: 'Returning Users', pick: (e) => Number(e?.returning?.users || 0), money: false },
    { key: 'newRevenue', label: 'New Revenue', pick: (e) => Number(e?.new?.revenue || 0), money: true },
    { key: 'returningRevenue', label: 'Returning Revenue', pick: (e) => Number(e?.returning?.revenue || 0), money: true }
  ];
  const activeSourceMetric = SOURCE_METRICS.find((m) => m.key === sourceMetric) || SOURCE_METRICS[0];

  // Top-5 channel split of new vs returning users for the same window as the
  // numbers above. Runs as a second GA4 report so the main view is never blocked.
  const fetchSources = async (start, end) => {
    if (!selectedBrand?.id || !start || !end) return;
    try {
      setSourcesLoading(true);
      setSourcesError('');
      const url = new URL(`${API_BASE}/api/google-analytics/ga4/new-vs-returning-sources`);
      url.searchParams.set('startDate', start);
      url.searchParams.set('endDate', end);
      url.searchParams.set('top', '5');
      const res = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      setSources(data);
    } catch (e) {
      setSources(null);
      setSourcesError(e?.message || 'Failed to load source breakdown');
    } finally {
      setSourcesLoading(false);
    }
  };

  const fetchRevenue = async () => {
    if (!selectedBrand?.id) return;
    if (!isBucketed && dateRange === 'custom' && (!customDates.startDate || !customDates.endDate)) {
      setError('Please select a start and end date.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      if (selectedBrand.id === DEMO_BRAND_ID) {
        const now = new Date();
        const monthKeys = Array.from({ length: 6 }, (_, idx) => {
          const d = new Date(now.getFullYear(), now.getMonth() - (5 - idx), 1);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        });
        const demo = [
          { totalRevenue: 30500000, returningShare: 0.60, newUserRatio: 0.78, returningUserRatio: 0.52, totalUserBase: 820000 },
          { totalRevenue: 140000000, returningShare: 0.70, newUserRatio: 0.75, returningUserRatio: 0.48, totalUserBase: 1120000 },
          { totalRevenue: 63000000, returningShare: 0.58, newUserRatio: 0.80, returningUserRatio: 0.50, totalUserBase: 940000 },
          { totalRevenue: 57000000, returningShare: 0.63, newUserRatio: 0.76, returningUserRatio: 0.51, totalUserBase: 880000 },
          { totalRevenue: 76000000, returningShare: 0.66, newUserRatio: 0.74, returningUserRatio: 0.49, totalUserBase: 780000 },
          { totalRevenue: 47000000, returningShare: 0.61, newUserRatio: 0.77, returningUserRatio: 0.53, totalUserBase: 480000 }
        ];
        const monthly = monthKeys.map((month, index) => {
          const seed = demo[index] || demo[demo.length - 1];
          const returningRevenue = Math.round(seed.totalRevenue * seed.returningShare);
          const newRevenue = Math.max(0, seed.totalRevenue - returningRevenue);
          const returningUsers = Math.round(seed.totalUserBase * seed.returningShare * seed.returningUserRatio);
          const newUsers = Math.round(seed.totalUserBase * (1 - seed.returningShare) * seed.newUserRatio);
          return {
            month,
            new: { revenue: newRevenue, users: newUsers },
            returning: { revenue: returningRevenue, users: returningUsers },
            notSet: { revenue: 0, users: 0 }
          };
        });
        const totals = monthly.reduce(
          (acc, row) => {
            acc.totalRevenue += row.new.revenue + row.returning.revenue;
            acc.totalUsers += row.new.users + row.returning.users;
            acc.newUsers += row.new.users;
            acc.returningUsers += row.returning.users;
            return acc;
          },
          { totalRevenue: 0, totalUsers: 0, newUsers: 0, returningUsers: 0 }
        );
        setPayload({
          success: true,
          granularity: 'monthly',
          dateRange: 'last6',
          startDate: monthKeys[0],
          endDate: monthKeys[monthKeys.length - 1],
          data: monthly,
          totals
        });
        const demoSources = [
          { key: 'google', label: 'Google', share: 0.42, newRatio: 0.68, paid: 0.74, topSources: [{ source: 'google' }, { source: 'youtube.com' }] },
          { key: 'meta', label: 'Meta', share: 0.24, newRatio: 0.74, paid: 0.86, topSources: [{ source: 'meta' }, { source: 'instagram.com' }] },
          { key: 'direct', label: 'Direct', share: 0.16, newRatio: 0.38, paid: 0, topSources: [{ source: '(direct)' }] },
          { key: 'tiktok', label: 'TikTok', share: 0.08, newRatio: 0.82, paid: 0.91, topSources: [{ source: 'tiktok' }] },
          { key: 'criteo', label: 'Criteo', share: 0.05, newRatio: 0.44, paid: 1, topSources: [{ source: 'criteo' }] },
          { key: 'other', label: 'Other', share: 0.05, newRatio: 0.55, paid: 0.2, isOther: true, groupCount: 34, topSources: [] }
        ];
        const buildDemoEntry = (seed, users, revenue) => {
          const newUsers = Math.round(users * seed.newRatio);
          const newRevenue = Math.round(revenue * seed.newRatio * 0.75);
          return {
            key: seed.key,
            label: seed.label,
            isOther: seed.isOther,
            groupCount: seed.groupCount,
            topSources: seed.topSources,
            new: { users: newUsers, sessions: Math.round(newUsers * 1.4), revenue: newRevenue },
            returning: {
              users: users - newUsers,
              sessions: Math.round((users - newUsers) * 2.1),
              revenue: revenue - newRevenue
            },
            notSet: { users: 0, sessions: 0, revenue: 0 },
            totalUsers: users,
            totalSessions: Math.round(users * 1.7),
            totalRevenue: revenue,
            paidUsers: Math.round(users * seed.paid),
            paidRevenue: Math.round(revenue * seed.paid)
          };
        };
        const demoSourceRows = demoSources.map((seed) => buildDemoEntry(
          seed,
          Math.round(totals.totalUsers * seed.share),
          Math.round(totals.totalRevenue * seed.share)
        ));
        const demoMonthly = monthKeys.map((month, index) => {
          const monthSeed = monthly[index];
          const monthUsers = monthSeed.new.users + monthSeed.returning.users;
          const monthRevenue = monthSeed.new.revenue + monthSeed.returning.revenue;
          const byKey = {};
          demoSources.forEach((seed) => {
            byKey[seed.key] = buildDemoEntry(
              seed,
              Math.round(monthUsers * seed.share),
              Math.round(monthRevenue * seed.share)
            );
          });
          const monthTotals = Object.values(byKey).reduce((acc, entry) => {
            acc.newUsers += entry.new.users;
            acc.returningUsers += entry.returning.users;
            acc.totalUsers += entry.totalUsers;
            acc.newRevenue += entry.new.revenue;
            acc.returningRevenue += entry.returning.revenue;
            acc.totalRevenue += entry.totalRevenue;
            return acc;
          }, { newUsers: 0, returningUsers: 0, totalUsers: 0, newRevenue: 0, returningRevenue: 0, totalRevenue: 0 });
          return { month, byKey, totals: monthTotals };
        });
        setSources({
          success: true,
          startDate: `${monthKeys[0]}-01`,
          endDate: `${monthKeys[monthKeys.length - 1]}-01`,
          top: 5,
          sourceCount: 39,
          columns: demoSources.map((seed) => ({
            key: seed.key,
            label: seed.label,
            isOther: seed.isOther,
            groupCount: seed.groupCount
          })),
          data: demoSourceRows,
          monthly: demoMonthly,
          totals: demoSourceRows.reduce((acc, row) => {
            acc.newUsers += row.new.users;
            acc.returningUsers += row.returning.users;
            acc.totalUsers += row.totalUsers;
            acc.newRevenue += row.new.revenue;
            acc.returningRevenue += row.returning.revenue;
            acc.totalRevenue += row.totalRevenue;
            return acc;
          }, { newUsers: 0, returningUsers: 0, totalUsers: 0, newRevenue: 0, returningRevenue: 0, totalRevenue: 0 })
        });
        setSourcesError('');
        setLastUpdated(new Date());
        return;
      }
      const url = new URL(`${API_BASE}/api/google-analytics/ga4/new-vs-returning-revenue`);
      url.searchParams.set('dateRange', isMonthly ? 'last6' : isWeekly ? 'last90' : dateRange);
      if (isMonthly) {
        url.searchParams.set('granularity', 'monthly');
        url.searchParams.set('months', '6');
      }
      if (isWeekly) {
        url.searchParams.set('granularity', 'weekly');
        url.searchParams.set('weeks', '12');
      }
      if (!isBucketed && dateRange === 'custom') {
        url.searchParams.set('startDate', customDates.startDate);
        url.searchParams.set('endDate', customDates.endDate);
      }
      const res = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      setPayload(data);
      setLastUpdated(new Date());
      fetchSources(data?.startDate, data?.endDate);
    } catch (e) {
      setPayload(null);
      setSources(null);
      setError(e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, dateRange, customDates.startDate, customDates.endDate, viewMode]);

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white text-gray-900 border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <img
                src="/GA4logoicon.svg"
                alt="GA4"
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">New vs Returning Revenue</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Revenue split by user type from GA4 (new vs returning users).
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setViewMode('summary')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'summary'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Summary
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('monthly');
                    setDateRange('last6');
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'monthly'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Monthly (6M)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('weekly');
                    setDateRange('last90');
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'weekly'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Weekly (12W)
                </button>
              </div>
              {!isBucketed && (
                <div className="relative">
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="pl-9 pr-4 py-2 bg-white border rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {DATE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              )}
              {!isBucketed && dateRange === 'custom' && (
                <>
                  <input
                    type="date"
                    value={customDates.startDate}
                    onChange={(e) => setCustomDates((prev) => ({ ...prev, startDate: e.target.value }))}
                    className="px-3 py-2 text-sm border rounded-lg"
                  />
                  <input
                    type="date"
                    value={customDates.endDate}
                    onChange={(e) => setCustomDates((prev) => ({ ...prev, endDate: e.target.value }))}
                    className="px-3 py-2 text-sm border rounded-lg"
                  />
                </>
              )}
              <button
                type="button"
                onClick={fetchRevenue}
                className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors"
                disabled={loading}
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="px-6 py-16 bg-white text-center text-gray-500">Loading revenue split...</div>
        ) : error ? (
          <div className="px-6 py-16 bg-white text-center text-red-600">{error}</div>
        ) : (!isBucketed && normalizedRows.length === 0) || (isBucketed && monthlyRows.length === 0) ? (
          <div className="px-6 py-16 bg-white text-center text-gray-500">No data for this period.</div>
        ) : (
          <div className="p-6 bg-white space-y-6">
            <div className="grid gap-4 md:grid-cols-6">
              <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="text-[11px] uppercase text-gray-500 tracking-wide">Total Revenue</div>
                <div className="mt-2 text-xl font-semibold text-gray-900">
                  {formatCurrency(displayTotals.totalRevenue, selectedBrand?.currency)}
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  {isMonthly ? 'Last 6 months' : isWeekly ? 'Last 12 weeks' : 'Selected period'}
                </div>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="text-[11px] uppercase text-gray-500 tracking-wide">Returning Share</div>
                <div className="mt-2 text-xl font-semibold text-gray-900">
                  {returningShare.toFixed(1)}%
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  {formatCurrency(revenueBreakdown.returning, selectedBrand?.currency)}
                </div>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="text-[11px] uppercase text-gray-500 tracking-wide">New Share</div>
                <div className="mt-2 text-xl font-semibold text-gray-900">
                  {newShare.toFixed(1)}%
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  {formatCurrency(revenueBreakdown.news, selectedBrand?.currency)}
                </div>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="text-[11px] uppercase text-gray-500 tracking-wide">Total Users</div>
                <div className="mt-2 text-xl font-semibold text-gray-900">
                  {displayTotals.totalUsers.toLocaleString()}
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  {isMonthly ? 'Last 6 months' : isWeekly ? 'Last 12 weeks' : 'Selected period'}
                </div>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="text-[11px] uppercase text-gray-500 tracking-wide">New Users</div>
                <div className="mt-2 text-xl font-semibold text-gray-900">
                  {(displayTotals.newUsers || 0).toLocaleString()}
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  {displayTotals.totalUsers > 0
                    ? `${((displayTotals.newUsers || 0) / displayTotals.totalUsers * 100).toFixed(1)}% of total`
                    : '—'}
                </div>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="text-[11px] uppercase text-gray-500 tracking-wide">Returning Users</div>
                <div className="mt-2 text-xl font-semibold text-gray-900">
                  {(displayTotals.returningUsers || 0).toLocaleString()}
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  {displayTotals.totalUsers > 0
                    ? `${((displayTotals.returningUsers || 0) / displayTotals.totalUsers * 100).toFixed(1)}% of total`
                    : '—'}
                </div>
              </div>
            </div>
            {!isBucketed ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User Type
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Revenue
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Users
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rev / User
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {normalizedRows.map((row) => (
                        <tr key={`${row.key}-row`}>
                          <td className="px-4 py-3 text-gray-900 font-medium">{row.label}</td>
                          <td className="px-4 py-3 text-right text-gray-900">
                            {formatCurrency(row.totalRevenue, selectedBrand?.currency)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {row.totalUsers.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {row.totalUsers > 0 ? formatCurrency(row.totalRevenue / row.totalUsers, selectedBrand?.currency) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <>
                {!isBucketedData ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
                    {isWeekly ? 'Weekly' : 'Monthly'} breakdown not available yet. Please refresh the API or restart the backend.
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {isWeekly ? 'Week' : 'Month'}
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-emerald-50/60">
                              Returning Revenue
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-emerald-50/60">
                              New Revenue
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-emerald-50/60">
                              Total Revenue
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-sky-50/60">
                              Returning Users
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-sky-50/60">
                              New Users
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-sky-50/60">
                              Total Users
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {monthlyRows.map((row) => {
                            const totalRevenue = row.totalRevenue || 0;
                            const returningRevenuePct = totalRevenue > 0 ? (row.returningRevenue / totalRevenue) * 100 : 0;
                            const newRevenuePct = totalRevenue > 0 ? (row.newRevenue / totalRevenue) * 100 : 0;
                            const totalUsers = row.returningUsers + row.newUsers + row.notSetUsers;
                            const returningUsersPct = totalUsers > 0 ? (row.returningUsers / totalUsers) * 100 : 0;
                            const newUsersPct = totalUsers > 0 ? (row.newUsers / totalUsers) * 100 : 0;
                            return (
                              <tr key={`${row.month}-combined`} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-900 font-medium">{row.bucketLabel || row.month}</td>
                                <td className="px-4 py-3 text-right text-gray-900 bg-emerald-50/60">
                                  <div className="text-gray-900">
                                    {formatCurrency(row.returningRevenue, selectedBrand?.currency)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {returningRevenuePct.toFixed(1)}% of revenue
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-900 bg-emerald-50/60">
                                  <div className="text-gray-900">
                                    {formatCurrency(row.newRevenue, selectedBrand?.currency)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {newRevenuePct.toFixed(1)}% of revenue
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-900 bg-emerald-50/60">
                                  <div className="text-gray-900">
                                    {formatCurrency(row.totalRevenue, selectedBrand?.currency)}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-900 bg-sky-50/60">
                                  <div className="text-gray-900">
                                    {row.returningUsers.toLocaleString()}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {returningUsersPct.toFixed(1)}% of users
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-900 bg-sky-50/60">
                                  <div className="text-gray-900">
                                    {row.newUsers.toLocaleString()}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {newUsersPct.toFixed(1)}% of users
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-900 bg-sky-50/60">
                                  <div className="text-gray-900">
                                    {totalUsers.toLocaleString()}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <Card className="mt-4 border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                      <div className="p-4 border-b bg-white">
                        <div className="text-sm font-semibold text-gray-900">Daily Average Users per Month</div>
                        <div className="text-xs text-gray-500">
                          Her ay için günlük ortalama yeni ve dönen kullanıcı. Geçmiş aylar için ayın toplam günü,
                          içinde bulunduğumuz ay için <span className="font-medium">bugüne kadar geçen gün sayısı</span> kullanılır
                          (bugün dahil değil).
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Month
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Days
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-sky-50/60">
                                New Users / Day
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-sky-50/60">
                                Returning Users / Day
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-sky-50/60">
                                Total Users / Day
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {monthlyRows.map((row) => {
                              // row.month = 'YYYY-MM'
                              const [y, m] = String(row.month || '').split('-').map(Number);
                              const now = new Date();
                              const curY = now.getFullYear();
                              const curM = now.getMonth() + 1;
                              const isCurrentMonth = y === curY && m === curM;
                              const daysInMonth = y && m ? new Date(y, m, 0).getDate() : 0;
                              // Current month: use elapsed days excluding today (today.getDate() - 1).
                              // Guard against day-1 of the month → fall back to 1.
                              const divisor = isCurrentMonth
                                ? Math.max(1, now.getDate() - 1)
                                : daysInMonth;
                              const totalUsers = row.newUsers + row.returningUsers + row.notSetUsers;
                              const newPerDay = divisor > 0 ? row.newUsers / divisor : 0;
                              const retPerDay = divisor > 0 ? row.returningUsers / divisor : 0;
                              const totalPerDay = divisor > 0 ? totalUsers / divisor : 0;
                              return (
                                <tr key={`${row.month}-per-day`} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-gray-900 font-medium">
                                    {row.month}
                                    {isCurrentMonth && (
                                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                        In progress
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-700 tabular-nums">
                                    {divisor}
                                    {isCurrentMonth && (
                                      <span className="text-xs text-gray-400"> / {daysInMonth}</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-900 bg-sky-50/60 tabular-nums">
                                    {Math.round(newPerDay).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-900 bg-sky-50/60 tabular-nums">
                                    {Math.round(retPerDay).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-900 bg-sky-50/60 tabular-nums">
                                    {Math.round(totalPerDay).toLocaleString()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </Card>

                    <Card className="mt-4 border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                      <div className="p-4 border-b bg-white">
                        <div className="text-sm font-semibold text-gray-900">Monthly Averages</div>
                        <div className="text-xs text-gray-500">Efficiency ratios by month</div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Month
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Rev / User (Total)
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Returning Rev / User
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                New Rev / User
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {monthlyRows.map((row) => {
                              const totalUsers = row.returningUsers + row.newUsers + row.notSetUsers;
                              const revenuePerUser = totalUsers > 0 ? row.totalRevenue / totalUsers : 0;
                              const returningRevenuePerUser = row.returningUsers > 0 ? row.returningRevenue / row.returningUsers : 0;
                              const newRevenuePerUser = row.newUsers > 0 ? row.newRevenue / row.newUsers : 0;
                              return (
                                <tr key={`${row.month}-averages`} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-gray-900 font-medium">{row.month}</td>
                                  <td className="px-4 py-3 text-right text-gray-900">
                                    {formatCurrency(revenuePerUser, selectedBrand?.currency)}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-900 bg-emerald-50/60">
                                    {formatCurrency(returningRevenuePerUser, selectedBrand?.currency)}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-900 bg-emerald-50/60">
                                    {formatCurrency(newRevenuePerUser, selectedBrand?.currency)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  </>
                )}
              </>
            )}

            {/* Platform split (Google / Meta / ...) of new vs returning users. */}
            <Card className="border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b bg-white flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Traffic Sources — New vs Returning</div>
                  <div className="text-xs text-gray-500">
                    GA4 <span className="font-medium">session source</span> platform bazında toplanır (Google, Meta, …);
                    en büyük 5 kaynak listelenir, kalanlar “Other” satırında birleşir.
                    {sources?.startDate && sources?.endDate ? ` ${sources.startDate} → ${sources.endDate}` : ''}
                  </div>
                </div>
                {sources?.sourceCount > 0 && (
                  <div className="text-[11px] text-gray-400">
                    {sources.sourceCount.toLocaleString()} kaynak bulundu
                  </div>
                )}
              </div>
              {sourcesLoading ? (
                <div className="px-4 py-10 text-center text-sm text-gray-500">Loading source breakdown...</div>
              ) : sourcesError ? (
                <div className="px-4 py-10 text-center text-sm text-red-600">{sourcesError}</div>
              ) : sourceRows.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-500">No source data for this period.</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Source
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                            New / Returning Split
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-sky-50/60">
                            New Users
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-sky-50/60">
                            Returning Users
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-sky-50/60">
                            Total Users
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-emerald-50/60">
                            New Revenue
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-emerald-50/60">
                            Returning Revenue
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-emerald-50/60">
                            Total Revenue
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {sourceRows.map((row) => {
                          const usersShare = sourceTotals?.totalUsers > 0
                            ? (row.totalUsers / sourceTotals.totalUsers) * 100
                            : 0;
                          const covered = row.topSources.map((s) => s.source).join(', ');
                          return (
                            <tr key={`source-${row.key}`} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">
                                {row.label}
                                {row.isOther && row.groupCount > 0 && (
                                  <span className="ml-2 text-[11px] font-normal text-gray-400">
                                    {row.groupCount.toLocaleString()} kaynak
                                  </span>
                                )}
                                {covered && (
                                  <div className="text-[11px] font-normal text-gray-400 max-w-[220px] truncate" title={covered}>
                                    {covered}
                                  </div>
                                )}
                                {row.paidSharePct > 0 && (
                                  <div className="text-[11px] font-normal text-gray-400">
                                    %{row.paidSharePct.toFixed(0)} ücretli
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex h-2 w-40 overflow-hidden rounded-full bg-gray-100">
                                  <div
                                    className="bg-sky-500"
                                    style={{ width: `${row.newSharePct}%` }}
                                    title={`New ${row.newSharePct.toFixed(1)}%`}
                                  />
                                  <div
                                    className="bg-emerald-500"
                                    style={{ width: `${row.returningSharePct}%` }}
                                    title={`Returning ${row.returningSharePct.toFixed(1)}%`}
                                  />
                                </div>
                                <div className="mt-1 text-[11px] text-gray-500">
                                  {row.newSharePct.toFixed(0)}% new · {row.returningSharePct.toFixed(0)}% returning
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right bg-sky-50/60 tabular-nums">
                                <div className="text-gray-900">{row.newUsers.toLocaleString()}</div>
                                <div className="text-xs text-gray-500">{row.newSharePct.toFixed(1)}% of source</div>
                              </td>
                              <td className="px-4 py-3 text-right bg-sky-50/60 tabular-nums">
                                <div className="text-gray-900">{row.returningUsers.toLocaleString()}</div>
                                <div className="text-xs text-gray-500">{row.returningSharePct.toFixed(1)}% of source</div>
                              </td>
                              <td className="px-4 py-3 text-right bg-sky-50/60 tabular-nums">
                                <div className="text-gray-900">{row.totalUsers.toLocaleString()}</div>
                                <div className="text-xs text-gray-500">{usersShare.toFixed(1)}% of all users</div>
                              </td>
                              <td className="px-4 py-3 text-right text-gray-900 bg-emerald-50/60 tabular-nums">
                                {formatCurrency(row.newRevenue, selectedBrand?.currency)}
                              </td>
                              <td className="px-4 py-3 text-right text-gray-900 bg-emerald-50/60 tabular-nums">
                                {formatCurrency(row.returningRevenue, selectedBrand?.currency)}
                              </td>
                              <td className="px-4 py-3 text-right text-gray-900 bg-emerald-50/60 tabular-nums">
                                {formatCurrency(row.totalRevenue, selectedBrand?.currency)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {sourceTotals && (
                        <tfoot className="bg-gray-50 border-t border-gray-200">
                          <tr className="font-medium text-gray-900">
                            <td className="px-4 py-3">All sources</td>
                            <td className="px-4 py-3" />
                            <td className="px-4 py-3 text-right tabular-nums">
                              {(sourceTotals.newUsers || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {(sourceTotals.returningUsers || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {(sourceTotals.totalUsers || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {formatCurrency(sourceTotals.newRevenue, selectedBrand?.currency)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {formatCurrency(sourceTotals.returningRevenue, selectedBrand?.currency)}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              {formatCurrency(sourceTotals.totalRevenue, selectedBrand?.currency)}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>

                  {sourceMonthly.length > 0 && sourceColumns.length > 0 && (
                    <div className="border-t border-gray-200">
                      <div className="p-4 flex flex-wrap items-center justify-between gap-3 bg-white">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Monthly Breakdown by Source</div>
                          <div className="text-xs text-gray-500">
                            Aynı kaynak sıralaması, ay ay. Yüzdeler ayın kendi toplamına göredir.
                          </div>
                        </div>
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                          {SOURCE_METRICS.map((metric) => (
                            <button
                              key={metric.key}
                              type="button"
                              onClick={() => setSourceMetric(metric.key)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                sourceMetric === metric.key
                                  ? 'bg-white text-blue-600 shadow-sm'
                                  : 'text-gray-600 hover:text-gray-800'
                              }`}
                            >
                              {metric.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-y border-gray-200">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Month
                              </th>
                              {sourceColumns.map((col) => (
                                <th
                                  key={`mcol-${col.key}`}
                                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                >
                                  {col.label}
                                </th>
                              ))}
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {sourceMonthly.map((monthRow) => {
                              const values = sourceColumns.map((col) => activeSourceMetric.pick(monthRow?.byKey?.[col.key]));
                              const rowTotal = values.reduce((sum, v) => sum + v, 0);
                              return (
                                <tr key={`msrc-${monthRow.month}`} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">
                                    {monthRow.month}
                                  </td>
                                  {values.map((value, idx) => (
                                    <td
                                      key={`msrc-${monthRow.month}-${sourceColumns[idx].key}`}
                                      className="px-4 py-3 text-right tabular-nums"
                                    >
                                      <div className="text-gray-900">
                                        {activeSourceMetric.money
                                          ? formatCurrency(value, selectedBrand?.currency)
                                          : value.toLocaleString()}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {rowTotal > 0 ? `${((value / rowTotal) * 100).toFixed(1)}%` : '—'}
                                      </div>
                                    </td>
                                  ))}
                                  <td className="px-4 py-3 text-right text-gray-900 font-medium bg-gray-50 tabular-nums">
                                    {activeSourceMetric.money
                                      ? formatCurrency(rowTotal, selectedBrand?.currency)
                                      : rowTotal.toLocaleString()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>

            <div className="text-xs text-gray-500">
              {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Not updated yet'}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
